import MedenticAuthEntryPage from "@shared/ui/MedenticAuthEntryPage";
import { UserPermissionAccess } from "@shared/types/commonTypes";
import { ROOTS } from "@app/config/constants";
import { useNavigate } from "react-router-dom";
import {
  ChevronRightIcon,
  CalendarTimeIcon,
  EmailIcon,
  StarIcon,
} from "@medentic/polaris-icons";
import {
  BlockStack,
  Box,
  Icon,
  InlineStack,
  ResourceItem,
  ResourceList,
  Text,
} from "@medentic/polaris-react";
import { useSelector } from "react-redux";
import { selectUserPermissions } from "@features/user/model/userSlice";
import { useEffect, useState } from "react";
import { useGetMyCompanies } from "@shared/hooks/common/useGetMyCompanies";
import { HealthcareCompany, HealthcareCompanyType } from "@shared/types/commonTypes";
import { setLocalStorage } from "@shared/lib";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "@tanstack/react-query";
function AppModeSelector() {
  const navigate = useNavigate();
  const permissions = useSelector(selectUserPermissions);
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  // State to track the selected mode
  const [selectedMode, setSelectedMode] = useState<string | null>(null);

  // Create app mode items based on permissions
  const appModeItems = [];
  const hasClinicAccess = permissions.includes(UserPermissionAccess.CLINIC);
  const hasEventAccess = permissions.includes(UserPermissionAccess.EVENT);

  // Try to get cached data first (from useInitializeApp)
  const cachedAllCompanies = queryClient.getQueryData<HealthcareCompany[]>([
    "healthcare-companies",
  ]);

  // Only fetch if not in cache and mode is selected
  const { companies: allCompanies, isLoading: isCompaniesLoading } = useGetMyCompanies({
    // No type filter - fetch all companies in one call
    enabled: selectedMode !== null && selectedMode !== ROOTS.PATIENT && !cachedAllCompanies && (hasClinicAccess || hasEventAccess),
  });

  // Use cached or fetched data
  const finalAllCompanies = allCompanies ?? cachedAllCompanies ?? [];

  // Split companies by type client-side
  const finalClinics = finalAllCompanies.filter(
    (company) => company.type === HealthcareCompanyType.DENTAL_CLINIC
  );
  const finalEventOrganizers = finalAllCompanies.filter(
    (company) => company.type === HealthcareCompanyType.EVENT_ORGANIZER
  );

  // Calculate overall loading state
  const isLoading = selectedMode !== null && selectedMode !== ROOTS.PATIENT && isCompaniesLoading;

  // Effect to perform navigation when data is loaded
  useEffect(() => {
    // For clinic mode - navigate to list page, not auto-redirect to specific clinic
    if (selectedMode === ROOTS.CLINIC && !isCompaniesLoading && finalClinics) {
      setLocalStorage("LastSelectedAppMode", ROOTS.CLINIC);
      navigate(ROOTS.CLINIC);
    }

    // For events mode - navigate to list page, not auto-redirect to specific organizer
    if (
      selectedMode === ROOTS.EVENTS &&
      !isCompaniesLoading &&
      finalEventOrganizers
    ) {
      setLocalStorage("LastSelectedAppMode", ROOTS.EVENTS);
      navigate(ROOTS.EVENTS);
    }
  }, [
    selectedMode,
    isCompaniesLoading,
    finalClinics,
    finalEventOrganizers,
    navigate,
  ]);

  if (hasClinicAccess) {
    appModeItems.push({
      id: ROOTS.CLINIC,
      title: t("appModeSelector.clinicMode"),
      icon: CalendarTimeIcon,
    });
  }

  if (hasEventAccess) {
    appModeItems.push({
      id: ROOTS.EVENTS,
      title: t("appModeSelector.eventsMode"),
      icon: StarIcon,
    });
  }

  if (
    permissions.includes("hasRecords") ||
    permissions.includes("hasInvoices")
  ) {
    appModeItems.push({
      id: ROOTS.PATIENT,
      title: t("appModeSelector.patientMode"),
      icon: EmailIcon,
    });
  }

  // Function to handle mode selection
  const handleModeSelection = (mode: string) => {
    if (mode === ROOTS.PATIENT) {
      // Patient mode doesn't need data fetching
      navigate(ROOTS.PATIENT);
    } else {
      // Set selected mode to trigger data fetching
      setSelectedMode(mode);
    }
  };

  return (
    <MedenticAuthEntryPage>
      <BlockStack gap="200">
        <Text variant="headingLg" as="h2">
          {t("appModeSelector.title")}
        </Text>
        <Text as="span" variant="bodySm" tone="subdued">
          {t("appModeSelector.subtitle")}
        </Text>
      </BlockStack>
      <Box paddingBlockEnd={"600"} />

      <ResourceList
        loading={isLoading}
        resourceName={{
          singular: t("appModeSelector.mode"),
          plural: t("appModeSelector.modes"),
        }}
        items={appModeItems}
        renderItem={(item) => {
          const { id, title } = item;

          return (
            <ResourceItem
              onClick={() => handleModeSelection(id)}
              id={id}
              key={id}
              accessibilityLabel={t("appModeSelector.viewDetailsFor", {
                title,
              })}
              disabled={isLoading}
            >
              <InlineStack
                align="space-between"
                wrap={false}
                blockAlign="center"
              >
                <InlineStack align="space-between" gap={"100"}>
                  <BlockStack gap={"100"}>
                    <InlineStack align="start" gap={"200"}>
                      <Text variant="bodyMd" fontWeight="bold" as="h3">
                        {title}
                      </Text>
                    </InlineStack>
                  </BlockStack>
                </InlineStack>
                <Box>
                  <Icon source={ChevronRightIcon} />
                </Box>
              </InlineStack>
            </ResourceItem>
          );
        }}
      />
      <Box paddingBlockEnd={"500"} />
    </MedenticAuthEntryPage>
  );
}

export default AppModeSelector;
